import { useState, useEffect, useCallback } from 'react';
import { campaignsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { useSortableTable } from '../hooks/useSortableTable';
import {
  Loader2, Plus, Search, Users, Filter, Trash2, X, Save, Eye,
  Pencil, ArrowLeft, Zap,
} from 'lucide-react';
import { MobileFAB } from '../components/MobileFAB';
import { toast } from 'sonner';

const AUDIENCE_SORT_COLUMNS = [
  { key: 'name', type: 'text' },
  { key: 'updated_at', type: 'date' },
];

const FILTER_FIELDS = [
  { value: 'customer_type', label: 'Customer Type', type: 'select', options: ['residential', 'business'], category: 'Customer' },
  { value: 'customer_tag', label: 'Customer Tag', type: 'string', category: 'Customer' },
  { value: 'customer_created_date', label: 'Customer Created Date', type: 'date', category: 'Customer' },
  { value: 'last_job_date', label: 'Last Job Date (days ago)', type: 'number', category: 'Customer' },
  { value: 'zip_code', label: 'ZIP Code', type: 'string', category: 'Location' },
  { value: 'city', label: 'City', type: 'string', category: 'Location' },
  { value: 'state', label: 'State', type: 'string', category: 'Location' },
  { value: 'estimate_status', label: 'Estimate Status', type: 'select', options: ['draft', 'sent', 'approved', 'rejected'], category: 'Estimates' },
  { value: 'estimate_age_days', label: 'Estimate Age (days)', type: 'number', category: 'Estimates' },
  { value: 'estimate_total', label: 'Estimate Total ($)', type: 'number', category: 'Estimates' },
  { value: 'job_status', label: 'Job Status', type: 'select', options: ['scheduled', 'in_progress', 'paused', 'completed', 'cancelled'], category: 'Jobs' },
  { value: 'job_type', label: 'Job Type', type: 'string', category: 'Jobs' },
  { value: 'job_count', label: 'Job Count', type: 'number', category: 'Jobs' },
  { value: 'invoice_status', label: 'Invoice Status', type: 'select', options: ['draft', 'sent', 'paid', 'overdue', 'void'], category: 'Invoices' },
  { value: 'total_customer_spend', label: 'Total Customer Spend ($)', type: 'number', category: 'Invoices' },
];

const OPERATORS = {
  string: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'in', label: 'in (comma-separated)' },
  ],
  number: [
    { value: 'gt', label: 'greater than' },
    { value: 'lt', label: 'less than' },
    { value: 'gte', label: 'at least' },
    { value: 'lte', label: 'at most' },
    { value: 'equals', label: 'equals' },
  ],
  date: [
    { value: 'after', label: 'after' },
    { value: 'before', label: 'before' },
  ],
  select: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
  ],
};

export default function Audiences() {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [previews, setPreviews] = useState({});
  const [creatingPreset, setCreatingPreset] = useState(null);

  const PRESETS = [
    {
      key: 'dormant_90',
      name: 'Dormant Customers (90+ days)',
      description: 'Customers with no jobs in the last 90 days — ideal for reactivation campaigns.',
      filters: [{ field: 'last_job_date', operator: 'gt', value: '90', logic_group: 0 }],
    },
    {
      key: 'completed_30',
      name: 'Completed Jobs — Last 30 Days',
      description: 'Customers whose jobs were completed in the last 30 days — great for review requests.',
      filters: [
        { field: 'job_status', operator: 'equals', value: 'completed', logic_group: 0 },
        { field: 'last_job_date', operator: 'lt', value: '30', logic_group: 0 },
      ],
    },
    {
      key: 'unsold_estimates_7',
      name: 'Unsold Estimates (7+ days)',
      description: 'Customers with sent estimates older than 7 days that haven\'t been approved.',
      filters: [
        { field: 'estimate_status', operator: 'equals', value: 'sent', logic_group: 0 },
        { field: 'estimate_age_days', operator: 'gt', value: '7', logic_group: 0 },
      ],
    },
  ];

  const handleCreatePreset = async (preset) => {
    const exists = audiences.some(a => a.name === preset.name);
    if (exists) { toast.info(`"${preset.name}" already exists`); return; }
    setCreatingPreset(preset.key);
    try {
      await campaignsAPI.createAudience({
        name: preset.name,
        description: preset.description,
        filters: preset.filters,
      });
      toast.success(`Created "${preset.name}"`);
      fetchAudiences();
    } catch { toast.error('Failed to create audience'); }
    finally { setCreatingPreset(null); }
  };

  const fetchAudiences = useCallback(async () => {
    try {
      const res = await campaignsAPI.listAudiences();
      setAudiences(res.data);
    } catch { toast.error('Failed to load audiences'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAudiences(); }, [fetchAudiences]);

  const filtered = audiences.filter(a =>
    !search || a.name.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted: sortedAudiences, sortKey, sortDir, onSort } = useSortableTable(filtered, { columns: AUDIENCE_SORT_COLUMNS });

  const handlePreview = async (id) => {
    try {
      const res = await campaignsAPI.previewAudience(id);
      setPreviews(p => ({ ...p, [id]: res.data }));
    } catch { toast.error('Preview failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this audience? Campaigns using it will lose their audience selection.')) return;
    try {
      await campaignsAPI.deleteAudience(id);
      setAudiences(a => a.filter(x => x.id !== id));
      toast.success('Audience deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (editingId !== null) {
    return (
      <AudienceEditor
        audienceId={editingId}
        onBack={() => { setEditingId(null); fetchAudiences(); }}
      />
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="audiences-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="audiences-title">
              <Users className="w-6 h-6" />
              Audiences
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              {audiences.length} saved segment{audiences.length !== 1 ? 's' : ''} for campaigns and automations
            </p>
          </div>
          <Button onClick={() => setEditingId('new')} data-testid="create-audience-btn" className="hidden md:inline-flex">
            <Plus className="w-4 h-4 mr-2" /> New Audience
          </Button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search audiences..."
              className="pl-10"
              data-testid="search-audiences"
            />
          </div>
        </div>

        {/* Quick Presets */}
        {audiences.length < 10 && (
          <div data-testid="audience-presets">
            <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-2">Quick Presets</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PRESETS.map(p => {
                const exists = audiences.some(a => a.name === p.name);
                return (
                  <button
                    key={p.key}
                    onClick={() => !exists && handleCreatePreset(p)}
                    disabled={exists || creatingPreset === p.key}
                    className={`text-left border rounded-lg p-3 transition-all ${exists ? 'opacity-50 cursor-default bg-muted/30' : 'hover:border-primary/40 hover:bg-muted/20 cursor-pointer'}`}
                    data-testid={`preset-${p.key}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {creatingPreset === p.key ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                      ) : (
                        <Zap className="w-3.5 h-3.5 text-amber-500" />
                      )}
                      <span className="text-sm font-medium truncate">{p.name}</span>
                      {exists && <Badge variant="outline" className="ml-auto text-[10px] shrink-0">Added</Badge>}
                    </div>
                    <p className="text-[11px] text-muted-foreground/70 line-clamp-2">{p.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border rounded-lg p-12 text-center">
            <Users className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <h3 className="text-sm font-medium text-muted-foreground">No audiences yet</h3>
            <p className="text-xs text-muted-foreground/60 mt-1">Create your first audience to target specific customer segments</p>
            <Button size="sm" className="mt-4" onClick={() => setEditingId('new')} data-testid="empty-create-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Create Audience
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="name" onSort={onSort} className="min-w-[180px]">Name</SortableTableHead>
                  <TableHead className="min-w-[200px] hidden md:table-cell">Description</TableHead>
                  <TableHead className="w-28 text-center">Filters</TableHead>
                  <TableHead className="w-36 text-center">Matching</TableHead>
                  <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="updated_at" onSort={onSort} className="w-40 hidden lg:table-cell">Updated</SortableTableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAudiences.map(a => {
                  const preview = previews[a.id];
                  return (
                    <TableRow key={a.id} data-testid={`audience-row-${a.id}`}>
                      <TableCell>
                        <span className="font-medium text-sm">{a.name}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="text-sm text-muted-foreground truncate block max-w-[250px]">{a.description || '—'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="text-xs">
                          {a.filters?.length || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {preview ? (
                          <Badge variant="outline" className="text-xs">
                            {preview.count} customer{preview.count !== 1 ? 's' : ''}
                          </Badge>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground" onClick={() => handlePreview(a.id)} data-testid={`preview-audience-${a.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> Count
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-xs text-muted-foreground">
                          {a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingId(a.id)} data-testid={`edit-audience-${a.id}`}>
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(a.id)} data-testid={`delete-audience-${a.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <MobileFAB onClick={() => setEditingId('new')} data-testid="mobile-fab-audience" />
    </Layout>
  );
}


function AudienceEditor({ audienceId, onBack }) {
  const isNew = audienceId === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const res = await campaignsAPI.getAudience(audienceId);
          setName(res.data.name);
          setDescription(res.data.description || '');
          setFilters(res.data.filters || []);
        } catch { toast.error('Failed to load audience'); }
        finally { setLoading(false); }
      })();
    }
  }, [audienceId, isNew]);

  const addFilter = () => {
    setFilters(f => [...f, { field: 'customer_type', operator: 'equals', value: '', logic_group: 0 }]);
  };

  const updateFilter = (idx, updates) => {
    setFilters(f => f.map((x, i) => i === idx ? { ...x, ...updates } : x));
  };

  const removeFilter = (idx) => {
    setFilters(f => f.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        filters: filters.map(f => ({
          field: f.field, operator: f.operator, value: f.value, logic_group: f.logic_group || 0,
        })),
      };
      if (isNew) {
        await campaignsAPI.createAudience(payload);
        toast.success('Audience created');
      } else {
        await campaignsAPI.updateAudience(audienceId, payload);
        toast.success('Audience updated');
      }
      onBack();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handlePreview = async () => {
    if (isNew) {
      toast.info('Save the audience first to preview');
      return;
    }
    try {
      const res = await campaignsAPI.previewAudience(audienceId);
      setPreview(res.data);
    } catch { toast.error('Preview failed'); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="audience-editor">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold">{isNew ? 'New Audience' : 'Edit Audience'}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Details + Filters */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Name</label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Unsold Estimates 30+ days" className="mt-1" data-testid="audience-name" />
                </div>
                <div>
                  <label className="text-xs font-medium">Description</label>
                  <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What defines this audience?" rows={2} className="mt-1" data-testid="audience-description" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Filter className="w-4 h-4" /> Filters
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={addFilter} data-testid="add-filter-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Filter
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Filters in the same group are AND-ed. Different groups are OR-ed.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {filters.length === 0 ? (
                  <div className="text-center py-6">
                    <Filter className="w-6 h-6 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-xs text-muted-foreground">No filters — this audience matches all active customers</p>
                  </div>
                ) : (
                  filters.map((f, idx) => (
                    <FilterRow key={idx} filter={f} index={idx} onUpdate={updateFilter} onRemove={removeFilter} />
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Preview + Actions */}
          <div className="space-y-4">
            {preview && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="py-4">
                  <p className="text-sm font-medium text-blue-900">
                    {preview.count} customer{preview.count !== 1 ? 's' : ''} match
                  </p>
                  {preview.sample_names?.length > 0 && (
                    <p className="text-xs text-blue-700 mt-1">Sample: {preview.sample_names.join(', ')}</p>
                  )}
                </CardContent>
              </Card>
            )}
            <div className="space-y-2">
              {!isNew && (
                <Button variant="outline" className="w-full" onClick={handlePreview} data-testid="preview-btn">
                  <Eye className="w-4 h-4 mr-2" /> Preview Audience
                </Button>
              )}
              <Button className="w-full" onClick={handleSave} disabled={saving} data-testid="save-audience-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {isNew ? 'Create Audience' : 'Save Changes'}
              </Button>
              <Button variant="ghost" className="w-full" onClick={onBack}>Cancel</Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}


function FilterRow({ filter, index, onUpdate, onRemove }) {
  const fieldDef = FILTER_FIELDS.find(f => f.value === filter.field) || {};
  const ops = OPERATORS[fieldDef.type || 'string'] || OPERATORS.string;

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border bg-muted/20" data-testid={`filter-row-${index}`}>
      {index > 0 && (
        <Badge variant="outline" className="text-[9px] shrink-0">AND</Badge>
      )}
      <Select value={filter.field} onValueChange={v => onUpdate(index, { field: v, operator: 'equals', value: '' })}>
        <SelectTrigger className="h-8 text-xs w-48" data-testid={`filter-field-${index}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {['Customer', 'Location', 'Estimates', 'Jobs', 'Invoices'].map(cat => {
            const fields = FILTER_FIELDS.filter(f => f.category === cat);
            if (!fields.length) return null;
            return (
              <div key={cat}>
                <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">{cat}</div>
                {fields.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </div>
            );
          })}
        </SelectContent>
      </Select>
      <Select value={filter.operator} onValueChange={v => onUpdate(index, { operator: v })}>
        <SelectTrigger className="h-8 text-xs w-32" data-testid={`filter-op-${index}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {ops.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {fieldDef.type === 'select' ? (
        <Select value={filter.value} onValueChange={v => onUpdate(index, { value: v })}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`filter-value-${index}`}><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>
            {(fieldDef.options || []).map(o => (
              <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1).replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : fieldDef.type === 'date' ? (
        <Input
          type="date"
          value={filter.value}
          onChange={e => onUpdate(index, { value: e.target.value })}
          className="h-8 text-xs flex-1"
          data-testid={`filter-value-${index}`}
        />
      ) : (
        <Input
          value={filter.value}
          onChange={e => onUpdate(index, { value: e.target.value })}
          placeholder={fieldDef.type === 'number' ? '0' : 'Value...'}
          type={fieldDef.type === 'number' ? 'number' : 'text'}
          className="h-8 text-xs flex-1"
          data-testid={`filter-value-${index}`}
        />
      )}
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => onRemove(index)} data-testid={`remove-filter-${index}`}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
